import React, { useState } from "react";

import { useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";

import { RadioGroup } from "~/components/ui/radio-group";
import { supabase } from "~/integrations/supabase/client";
import SalesRequestCard from "~/pages/payments/components/SalesRequestCard";
import { resolveGross } from "~/utils/iva";
import { calculateVerifiedPayments } from "~/utils/paymentVerification";

interface SalesRequestItem {
  id: string;
  productName: string;
  quantity: number;
  unitPrice: number;
  unit?: string;
}

interface EnhancedSalesRequest {
  id: string;
  createdAt: string;
  totalAmount: number;
  paidAmount: number;
  items: SalesRequestItem[];
}

interface SalesRequestSelectorProps {
  clientId: string | undefined;
  value: string | undefined;
  onChange: (value: string) => void;
  error?: string;
}

const SalesRequestSelector: React.FC<SalesRequestSelectorProps> = ({
  clientId,
  value,
  onChange,
  error,
}) => {
  const [expandedSalesRequests, setExpandedSalesRequests] = useState<
    Record<string, boolean>
  >({});

  // Fetch sales requests for the selected client
  const { data: salesRequests, isLoading } = useQuery({
    queryKey: ["sales-requests-with-balance", clientId],
    queryFn: async () => {
      if (!clientId) return [];

      // Get sales requests that can have payments (exclude test/rejected statuses)
      const { data: requestsData, error: requestsError } = await supabase
        .from("sales_request")
        .select(
          "id, created_at, total_amount, paid_amount, price_with_iva, is_exento, status, paid"
        )
        .eq("client_id", clientId)
        .not("status", "in", "(test,rejected)")
        .order("created_at", { ascending: false });

      if (requestsError) {
        console.error("Error fetching sales requests:", requestsError);
        throw new Error("No se pudieron cargar las notas de venta");
      }

      // For each sales request, check if it has a remaining balance
      const enhancedRequests: EnhancedSalesRequest[] = [];

      for (const request of requestsData || []) {
        // Calculate verified payments to determine if there's a remaining balance
        const { verifiedAmount } = await calculateVerifiedPayments(request.id);

        // Gross (con IVA) owed; facturas exentas (SII 34) carry no IVA -> gross = net
        const totalAmountWithIva = resolveGross(request);

        // Only include sales requests with remaining balance
        const roundedVerifiedAmount = Math.round(verifiedAmount);
        const roundedTotalAmount = Math.round(totalAmountWithIva);
        const tolerance = 1; // 1 peso tolerance for rounding differences

        const hasRemainingBalance =
          roundedVerifiedAmount < roundedTotalAmount - tolerance;

        if (!hasRemainingBalance) {
          continue; // Skip fully paid sales requests
        }

        // Fetch items for this sales request
        const { data: itemsData, error: itemsError } = await supabase
          .from("sales_request_items")
          .select(
            `
            id,
            quantity,
            unit_price,
            discount_percent,
            products:product_id (
              short_name,
              unit_code,
              base_measurement_unit
            )
          `
          )
          .eq("sales_request_id", request.id);

        if (itemsError) {
          console.error("Error fetching sales request items:", itemsError);
          continue;
        }

        const items: SalesRequestItem[] = (itemsData || []).map((item) => ({
          id: item.id,
          productName:
            (item.products as any)?.short_name || "Producto desconocido",
          quantity: item.quantity,
          unitPrice: item.unit_price,
          discountPercent: item.discount_percent ?? undefined,
          unit:
            (item.products as any)?.unit_code ||
            (item.products as any)?.base_measurement_unit,
        }));

        enhancedRequests.push({
          id: request.id,
          createdAt: request.created_at,
          totalAmount: request.total_amount,
          paidAmount: request.paid_amount || 0,
          items,
        });
      }

      return enhancedRequests || [];
    },
    enabled: !!clientId,
  });

  const toggleSalesRequestDetails = (requestId: string) => {
    setExpandedSalesRequests((prev) => ({
      ...prev,
      [requestId]: !prev[requestId],
    }));
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center rounded-md border border-dashed p-4">
        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
        <span className="text-sm text-muted-foreground">
          Cargando notas de venta...
        </span>
      </div>
    );
  }

  if (!salesRequests?.length) {
    return (
      <div className="flex items-center justify-center rounded-md border border-dashed p-4 text-sm text-muted-foreground">
        No hay notas de venta pendientes de pago
      </div>
    );
  }

  return (
    <RadioGroup value={value} onValueChange={onChange} className="space-y-3">
      {salesRequests?.map((request) => (
        <SalesRequestCard
          key={request.id}
          id={request.id}
          createdAt={request.createdAt}
          totalAmount={request.totalAmount}
          paidAmount={request.paidAmount}
          items={request.items}
          isSelected={value === request.id}
          isExpanded={!!expandedSalesRequests[request.id]}
          onSelect={() => onChange(request.id)}
          onToggleExpand={() => toggleSalesRequestDetails(request.id)}
        />
      ))}
    </RadioGroup>
  );
};

export default SalesRequestSelector;
